import { z } from 'zod'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree'

// Schema pour un module assigné dans une assignment
export const AssignedModuleSchema = z.object({
  id: z.number().nonnegative(),
  assignmentId: z.number().positive().optional(), // Présent dans la réponse, mais pas dans le payload de création
  learningModuleId: z.number().positive(),
  learningModuleTitle: z.string().optional(), // Présent uniquement dans la réponse
  learningModuleOrder: z.number().optional(), // Présent uniquement dans la réponse
  learningCourseOrder: z.number().optional() // Présent uniquement dans la réponse
})

// Schema pour un module assigné dans la réponse fetchAssignmentsForClassroom (tous les champs présents)
export const AssignedModuleForClassroomSchema = z.object({
  id: z.number().nonnegative(),
  assignmentId: z.number().positive(),
  classroomId: z.number().positive(),
  learningModuleId: z.number().positive(),
  learningModuleTitle: z.string(),
  learningModuleOrder: z.number(),
  learningCourseId: z.number().positive(),
  learningCourseOrder: z.number(),
  order: z.number().nonnegative(),
  visible: z.boolean(),
  visibleAt: z.string().datetime().nullable().optional()
})

// Schema pour la création/mise à jour d'un assignment (payload)
export const AssignmentInputSchema = z.object({
  id: z.number().positive().optional(),
  schoolPeriodId: z.number().positive(),
  degree: z.nativeEnum(ClassroomDegree.Values),
  locked: z.boolean(),
  assignedModules: z.array(AssignedModuleSchema)
})

// Schema pour un assignment complet (réponse API)
export const AssignmentSchema = z.object({
  id: z.number().positive(),
  schoolPeriodId: z.number().positive(),
  degree: z.nativeEnum(ClassroomDegree.Values),
  locked: z.boolean(),
  assignedModules: z.array(AssignedModuleSchema)
})

// Schema pour un assignment dans la réponse fetchAssignmentsForClassroom
export const AssignmentForClassroomSchema = z.object({
  id: z.number().positive(),
  schoolPeriodId: z.number().positive(),
  degree: z.nativeEnum(ClassroomDegree.Values),
  locked: z.boolean(),
  assignedModules: z.array(AssignedModuleForClassroomSchema)
})

// Schema pour un module valide pour la matrice
export const ValidModuleForAssignmentSchema = z.object({
  id: z.number().positive(),
  title: z.string(),
  order: z.number(),
  learningCourseId: z.number().positive(),
  learningCourseTitle: z.string(),
  learningCourseOrder: z.number()
})

export const ClassroomAssignedModuleSchema = z.object({
  id: z.number().positive(),
  schoolAssignedModuleId: z.number().positive(),
  classroomId: z.number().positive(),
  order: z.number(),
  visible: z.boolean(),
  visibleAt: z.string().datetime().nullable()
})

// Types dérivés des schemas
export type AssignedModuleEntity = z.infer<typeof AssignedModuleSchema>
export type AssignedModuleForClassroomEntity = z.infer<typeof AssignedModuleForClassroomSchema>
export type AssignmentInputEntity = z.infer<typeof AssignmentInputSchema>
export type AssignmentEntity = z.infer<typeof AssignmentSchema>
export type AssignmentForClassroomEntity = z.infer<typeof AssignmentForClassroomSchema>
export type ValidModuleForAssignmentEntity = z.infer<typeof ValidModuleForAssignmentSchema>
export type ClassroomAssignedModuleEntity = z.infer<typeof ClassroomAssignedModuleSchema>

// Schemas pour les listes
export const AssignmentListSchema = z.array(AssignmentSchema)
export const ValidModuleForAssignmentListSchema = z.array(ValidModuleForAssignmentSchema)

export type AssignmentListEntity = z.infer<typeof AssignmentListSchema>
export type ValidModuleForAssignmentListEntity = z.infer<typeof ValidModuleForAssignmentListSchema>
